import styled from 'styled-components'
import { useCallback } from 'react'

import { Direction, SortBy, SortConfig, VaultListRow, VaultNumberUpdate } from '../types'
import { Typography } from 'components/Typography/Typography'
import { IconArrowUp } from 'components/Icons/IconArrowUp'
import { IconArrowDown } from 'components/Icons/IconArrowDown'
import { VaultRow } from './VaultRow'
import { Tooltip } from 'components/Tooltip/Tooltip'
import { IconInfo } from 'components/Icons/InfoIcon'
import { useIsMobile } from 'utils/useIsMobile'

interface Props {
  displayData: VaultListRow[]
  displayFrozenData: VaultListRow[]
  sortConfig: SortConfig
  sortConfigFrozen: SortConfig
  onColumnHeaderClick: (key: SortBy) => void
  onColumnFrozenHeaderClick: (key: SortBy) => void
  updateVaultInfoListNumbers: (updateObj: VaultNumberUpdate) => void
  isInitial: boolean
}

const VaultsHeader = styled.div<{ $isEmpty?: boolean }>`
  display: flex;
  position: relative;
  z-index: 1;
  padding: 24px 32px;
  border-bottom: ${p => (p.$isEmpty ? 'none' : `2px solid ${p.theme.colors.listHeaderBorder};`)}
  gap: 20px;
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    display: none;
  }
`

const Th = styled(Typography)<{ onClick?: () => void }>`
  flex-basis: 200px;
  cursor: pointer;
  display: flex;
  align-items: center;
  z-index: 10;
  & > div > svg {
    margin-left: 8px;
    & > path {
      stroke: ${p => p.theme.colors.text.primary};
    }
  }
`

const VaultTable = styled.div`
  border: 0.7px solid ${p => p.theme.colors.divider};
  background: ${p => p.theme.colors.background.primary};
  border-radius: 16px;
  @media screen and (min-width: ${p => p.theme.breakpoints.md}px) {
    box-shadow: 0px 0px 16px 0px ${p => p.theme.colors.background.hover};
    border-radius: 32px;
  }
  @media screen and (max-width: ${p => p.theme.breakpoints.md}px) {
    border: none;
  }
`

const TooltipSectionLabel = styled(Typography)`
  display: flex;
  align-items: center;
  margin-bottom: 12px;

  & > div {
    display: flex;
    cursor: pointer;
    & > svg {
      margin-left: 6px;
      width: 20px;
      height: 20px;
      path {
        stroke: ${p => p.theme.colors.text.secondary};
      }
    }
  }
`

export const VaultsList = ({
  displayData,
  sortConfig,
  onColumnHeaderClick,
  updateVaultInfoListNumbers,
  isInitial,
  sortConfigFrozen,
  displayFrozenData,
  onColumnFrozenHeaderClick,
}: Props) => {
  const isMobile = useIsMobile()

  const getSortIndicator = useCallback(
    (key: SortBy, isFrozen = false) => {
      if (isFrozen) {
        if (sortConfigFrozen.key === key) {
          return sortConfigFrozen.direction === Direction.Ascending ? <IconArrowUp /> : <IconArrowDown />
        }
        return null
      }
      if (sortConfig.key === key) {
        return sortConfig.direction === Direction.Ascending ? <IconArrowUp /> : <IconArrowDown />
      }
      return null
    },
    [sortConfig.direction, sortConfig.key, sortConfigFrozen.direction, sortConfigFrozen.key]
  )

  return (
    <>
      <VaultTable>
        <VaultsHeader $isEmpty={displayData.length === 0}>
          <Th variant="label1" fontWeight={sortConfig.key === SortBy.Vault ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnHeaderClick(SortBy.Vault)}>
              Vault
              {getSortIndicator(SortBy.Vault)}
            </div>
          </Th>
          <Th
            variant="label1"
            fontWeight={sortConfig.key === SortBy.TvlUsd ? 'semiBold' : 'regular'}
            style={{ flexBasis: 250 }}
          >
            <div onClick={() => onColumnHeaderClick(SortBy.TvlUsd)}>
              TVL
              {getSortIndicator(SortBy.TvlUsd)}
            </div>
          </Th>
          <Th
            variant="label1"
            fontWeight={sortConfig.key === SortBy.Apy ? 'semiBold' : 'regular'}
            style={{ flexBasis: 150 }}
          >
            <div onClick={() => onColumnHeaderClick(SortBy.Apy)}>
              APY
              {getSortIndicator(SortBy.Apy)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfig.key === SortBy.DepositedUsd ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnHeaderClick(SortBy.DepositedUsd)}>
              Deposited
              {getSortIndicator(SortBy.DepositedUsd)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfig.key === SortBy.EarnedUsd ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnHeaderClick(SortBy.EarnedUsd)}>
              Earned
              {getSortIndicator(SortBy.EarnedUsd)}
            </div>
          </Th>
        </VaultsHeader>
        <div>
          {displayData.map((info, index) => (
            <VaultRow
              isInitial={isInitial}
              key={info.tSymbol}
              displayInfo={info}
              updateNumbers={updateVaultInfoListNumbers}
              isLast={index === displayData.length - 1}
            />
          ))}
        </div>
      </VaultTable>
      <div style={{ height: 50 }}></div>
      <TooltipSectionLabel variant={isMobile ? 'label2' : 'subtitle2'} fontWeight="medium">
        Paused Vaults
        <Tooltip
          content="These vaults are paused as part of the response to the Cetus incident. A resolution is in progress."
          position="top"
          tooltipBoxStyles={isMobile ? { minWidth: 250 } : {}}
        >
          <IconInfo width={16} height={16} />
        </Tooltip>
      </TooltipSectionLabel>
      <VaultTable>
        <VaultsHeader $isEmpty={displayFrozenData.length === 0}>
          <Th variant="label1" fontWeight={sortConfigFrozen.key === SortBy.Vault ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnFrozenHeaderClick(SortBy.Vault)}>
              Vault
              {getSortIndicator(SortBy.Vault, true)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfigFrozen.key === SortBy.TvlUsd ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnFrozenHeaderClick(SortBy.TvlUsd)}>
              TVL
              {getSortIndicator(SortBy.TvlUsd, true)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfigFrozen.key === SortBy.Apy ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnFrozenHeaderClick(SortBy.Apy)}>
              APY
              {getSortIndicator(SortBy.Apy, true)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfigFrozen.key === SortBy.DepositedUsd ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnFrozenHeaderClick(SortBy.DepositedUsd)}>
              Deposited
              {getSortIndicator(SortBy.DepositedUsd, true)}
            </div>
          </Th>
          <Th variant="label1" fontWeight={sortConfigFrozen.key === SortBy.EarnedUsd ? 'semiBold' : 'regular'}>
            <div onClick={() => onColumnFrozenHeaderClick(SortBy.EarnedUsd)}>
              Earned
              {getSortIndicator(SortBy.EarnedUsd, true)}
            </div>
          </Th>
        </VaultsHeader>
        <div>
          {displayFrozenData.map((info, index) => (
            <VaultRow
              isInitial={isInitial}
              key={info.tSymbol}
              displayInfo={info}
              updateNumbers={updateVaultInfoListNumbers}
              isLast={index === displayData.length - 1}
              isFrozen={true}
            />
          ))}
        </div>
      </VaultTable>
    </>
  )
}
